/**
 * @flow
 */

import React, { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Icon } from 'semantic-ui-react';
import { omit } from 'lodash';
import { format } from 'date-fns';

import { Modal, Button } from '../../../../components';
import { SeminarForm } from '../form';
import { UPDATE_SEMINAR_QUERY } from '../../containers/queries';

const intlMessages = defineMessages({
    existingsError: {
        id: 'pages.seminars.form.SeminarForm.existingsError',
        defaultMessage: 'This seminar does not exists',
    },
    nameExistingsError: {
        id: 'pages.seminars.AddSeminarDialog.nameExistingsError',
        defaultMessage: 'Seminar with the same name already exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

function buildInitialValues(seminar) {
    return {
        name: seminar.name,
        description: seminar.description,
        startAt: seminar.startAt,
        duration: seminar.duration,
        placeCount: seminar.placeCount,
        type: seminar.type,
        eventTime: format(new Date(seminar.startAt), 'HH:mm'),
    };
}

function EditSeminar({ seminar, onClose, intl }) {
    const supplier = useSelector(({ user }) => user.get('id'));

    const methods = useForm({ defaultValues: buildInitialValues(seminar) });
    const { handleSubmit, reset, setError, formState: { isSubmitting, isDirty } } = methods;

    useEffect(() => {
        reset(buildInitialValues(seminar));
    }, [seminar.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const [updateSeminar] = useMutation(UPDATE_SEMINAR_QUERY);

    const onSubmit = async formData => {
        const startAt = `${format(new Date(formData.startAt), 'yyyy-MM-dd')} ${formData.eventTime}`;
        const placeCount = parseInt(formData.placeCount);
        const banner = formData.banner ? formData.banner[0] : undefined;

        try {
            await updateSeminar({
                variables: {
                    input: {
                        seminarId: seminar.id,
                        ...omit(formData, ['startAt', 'eventTime', 'placeCount', ...(banner ? [] : ['banner'])]),
                        placeCount,
                        supplier,
                        startAt,
                        ...(banner ? { banner } : {}),
                    },
                },
                optimisticResponse: {
                    __typename: 'Mutation',
                    updateSeminar: {
                        __typename: 'UpdateSeminarPayload',
                        seminar: {
                            __typename: 'Seminar',
                            id: seminar.id,
                            ...omit(formData, ['banner', 'startAt', 'eventTime', 'description']),
                            supplier,
                            startAt,
                            description: formData.description || '',
                            banner: { __typename: 'SeminarBanner', url: '' },
                            visitors: { count: 0, list: [] },
                        },
                    },
                },
            });
            onClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                if (graphQLError.name === 'NotExists' && graphQLError.data?.error === 'ID_DOES_NOT_EXISTS') {
                    setError('root', { message: intl.formatMessage(intlMessages.existingsError) });
                } else if (graphQLError.name === 'AlreadyExists' && graphQLError.data?.error === 'NAME_ALREADY_EXISTS') {
                    setError('name', { message: intl.formatMessage(intlMessages.nameExistingsError) });
                } else {
                    setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
                }
                return;
            }
            setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal.Context>
                <Modal.Content>
                    <SeminarForm seminar={seminar} />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.seminars.details.EditSeminar.save"
                            defaultMessage="Save"
                        />
                    </Button>
                    <Button onClick={onClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.seminars.details.EditSeminar.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal.Context>
        </FormProvider>
    );
}

export default injectIntl(EditSeminar);
